import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { Markdown } from '@client/components/_ui/display/Markdown';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { EmptyState } from '@client/components/_ui/feedback/EmptyState';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { QueryState } from '@client/components/_ui/feedback/QueryState';
import { SkeletonBlock } from '@client/components/_ui/feedback/SkeletonBlock';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import type { UploadFile, UploadProps } from 'antd';
import { Alert, Card, Collapse, Input, Typography, Upload } from 'antd';
import { Check, ChevronDown, FileText, Inbox, Pencil, RefreshCw, Save, Upload as UploadIcon, X } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/pds-text/$pdsId',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Manage PDS text',
        },
    },
    params: {
        parse({ pdsId }) {
            return {
                pdsId: BigInt(pdsId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <PdsTextEditor />
        </PageController>
    );
}

const { TextArea } = Input;
const { Dragger } = Upload;
const { Text, Paragraph } = Typography;

// ============================================================================
// Types
// ============================================================================

type InputMethod = 'upload' | 'paste';

type ExtractionState =
    | { status: 'idle' }
    | { status: 'uploading' }
    | { status: 'extracting' }
    | {
          status: 'preview';
          markdown: string;
          metadata: {
              pageCount: number;
              tableCount: number;
              paragraphCount: number;
              processingTimeMs: number;
              characterCount: number;
          };
      }
    | { status: 'error'; message: string };

// ============================================================================
// Main Component
// ============================================================================

/**
 * PDS Text Editor Overlay
 *
 * Allows administrators to manage the Product Disclosure Statement (PDS) text.
 * Supports two input methods:
 * 1. Upload PDF - Extract content from a PDF using Document AI
 * 2. Paste text - Direct text entry (legacy/fallback method)
 *
 * The raw text is processed by AI into an optimised format that the
 * Decision Engine uses for claim compliance assessment.
 */
function PdsTextEditor() {
    const { formatDateTime, formatNumber } = useFormatter();
    const orgId = useOrgId();
    const { pdsId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();

    const [rawText, setRawText] = useState('');
    const [inputMethod, setInputMethod] = useState<InputMethod>('upload');
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [extractionState, setExtractionState] = useState<ExtractionState>({ status: 'idle' });
    const [isEditingPreview, setIsEditingPreview] = useState(false);
    const [previewEditText, setPreviewEditText] = useState('');

    // Fetch PDS text data
    const pdsTextQuery = trpc.pds.getPsdText.useQuery(
        { id: pdsId },
        {
            refetchInterval: (query) => {
                // Poll every 3 seconds while processing
                return query.state.data?.psdProcessingStatus === 'PROCESSING' ? 3000 : false;
            },
        },
    );
    const { data, refetch } = pdsTextQuery;

    // Get presigned URL mutation
    const { mutateAsync: getUploadUrl } = trpc.file.getUploadPresignedUrl.useMutation({
        onError: (error) => {
            errorMessage.show(error.message);
        },
    });

    // Extract PDF mutation
    const { mutateAsync: extractPdf } = trpc.pds.extractPdfToMarkdown.useMutation({
        onError: (error) => {
            setExtractionState({ status: 'error', message: error.message });
        },
    });

    // Update mutation
    const { mutate: updatePdsText, isPending: isUpdating } = trpc.pds.updatePsdText.useMutation({
        onSuccess: () => {
            message.success('PDS text saved and processing started');
            void refetch();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    // Reprocess mutation
    const { mutate: reprocessPdsText, isPending: isReprocessing } = trpc.pds.reprocessPsdText.useMutation({
        onSuccess: () => {
            message.success('Reprocessing started');
            void refetch();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    // Cancel processing mutation
    const { mutate: cancelProcessing, isPending: isCancelling } = trpc.pds.cancelPdsProcessing.useMutation({
        onSuccess: () => {
            message.success('Processing cancelled');
            void refetch();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    // Sync local state with fetched data
    useEffect(() => {
        if (data?.psdRawText) {
            setRawText(data.psdRawText);
        } else {
            setRawText('');
        }
    }, [data?.psdRawText]);

    // ========================================================================
    // Handlers
    // ========================================================================

    const handleUploadAndExtract = async () => {
        if (fileList.length === 0) {
            errorMessage.show('Please select a PDF file to upload');
            return;
        }

        const file = fileList[0] as UploadFile & { originFileObj?: File };
        const actualFile = file.originFileObj || (file as unknown as File);

        setExtractionState({ status: 'uploading' });

        try {
            // Step 1: Get presigned upload URL
            const { url, fields } = await getUploadUrl({
                orgId: BigInt(orgId),
                filename: file.name ?? 'document.pdf',
                bucketType: 'private',
            });

            // Step 2: Upload file to S3
            const formData = new FormData();
            Object.entries(fields).forEach(([fieldKey, value]) => {
                formData.append(fieldKey, value as string);
            });
            formData.append('file', actualFile);

            const uploadResponse = await fetch(url, {
                method: 'POST',
                body: formData,
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file to storage');
            }

            // Step 3: Extract markdown from PDF
            setExtractionState({ status: 'extracting' });

            const result = await extractPdf({
                fileKey: fields.key as string,
                fileName: file.name ?? 'document.pdf',
                mimeType: 'application/pdf',
            });

            // Step 4: Show preview
            setExtractionState({
                status: 'preview',
                markdown: result.markdown,
                metadata: result.metadata,
            });
            setPreviewEditText(result.markdown);
        } catch (error) {
            console.error('Upload/extraction error:', error);
            setExtractionState({
                status: 'error',
                message: error instanceof Error ? error.message : 'Failed to extract PDF content',
            });
        }
    };

    const handleUseExtractedContent = () => {
        if (extractionState.status !== 'preview') return;

        const contentToUse = isEditingPreview ? previewEditText : extractionState.markdown;
        setRawText(contentToUse);
        setInputMethod('paste');
        setExtractionState({ status: 'idle' });
        setFileList([]);
        setIsEditingPreview(false);
        message.success('Extracted content applied. Review and save when ready.');
    };

    const handleCancelExtraction = () => {
        setExtractionState({ status: 'idle' });
        setFileList([]);
        setIsEditingPreview(false);
        setPreviewEditText('');
    };

    const handleSave = () => {
        updatePdsText({
            id: pdsId,
            psdRawText: rawText,
            processImmediately: true,
        });
    };

    const handleReprocess = () => {
        reprocessPdsText({ id: pdsId });
    };

    const handleCancelProcessing = () => {
        cancelProcessing({ id: pdsId });
    };

    // ========================================================================
    // Upload Props
    // ========================================================================

    const uploadProps: UploadProps = {
        name: 'file',
        multiple: false,
        fileList,
        accept: '.pdf',
        showUploadList: false,
        beforeUpload: (file) => {
            const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isPdf) {
                errorMessage.show('Only PDF files are supported');
                return Upload.LIST_IGNORE;
            }

            const isLt50M = file.size / 1024 / 1024 < 50;
            if (!isLt50M) {
                errorMessage.show('File must be smaller than 50MB');
                return Upload.LIST_IGNORE;
            }

            setFileList([file]);
            setExtractionState({ status: 'idle' });
            return false;
        },
        onRemove: () => {
            setFileList([]);
            setExtractionState({ status: 'idle' });
        },
    };

    // ========================================================================
    // Computed Values
    // ========================================================================

    const hasChanges = rawText !== (data?.psdRawText ?? '');
    const canReprocess = data?.hasRawText && !isReprocessing && !isUpdating;
    const isProcessing = data?.psdProcessingStatus === 'PROCESSING';
    const isExtracting = extractionState.status === 'uploading' || extractionState.status === 'extracting';
    const showPreview = extractionState.status === 'preview';
    const showError = extractionState.status === 'error';

    return (
        <OverlayPage
            size="large"
            title={
                <div className="flex items-center gap-3">
                    <span>Manage PDS text</span>
                    {data?.psdProcessingStatus && (
                        <StatusBadge status={data.psdProcessingStatus} module="pdsProcessing" variant="outlined" />
                    )}
                </div>
            }
            afterClose={() => {
                setRawText('');
                setFileList([]);
                setExtractionState({ status: 'idle' });
                setInputMethod('upload');
                setIsEditingPreview(false);
                setPreviewEditText('');
                void trpcUtils.pds.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <>
                    <Button type="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    {canReprocess && (
                        <Button
                            type="secondary"
                            icon={<RefreshCw size={16} />}
                            onClick={handleReprocess}
                            loading={isReprocessing}
                            disabled={isProcessing}
                        >
                            Reprocess
                        </Button>
                    )}
                    <Button
                        type="primary"
                        icon={<Save size={16} />}
                        onClick={handleSave}
                        loading={isUpdating}
                        disabled={!rawText.trim() || isProcessing}
                    >
                        Save and process
                    </Button>
                </>
            )}
        >
            <QueryState query={pdsTextQuery} loading={<SkeletonBlock variant="card" rows={10} />}>
                {() => (
                    <div className="space-y-4">
                        {/* PDS Info */}
                        <Card size="small" className="bg-muted">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <Text type="secondary">PDS name:</Text>
                                    <div className="font-medium">{data?.name}</div>
                                </div>
                                <div>
                                    <Text type="secondary">Insurer:</Text>
                                    <div className="font-medium">{data?.insurerName}</div>
                                </div>
                                <div>
                                    <Text type="secondary">Product type:</Text>
                                    <div className="font-medium">{data?.productType}</div>
                                </div>
                                {data?.psdProcessedAt && (
                                    <div>
                                        <Text type="secondary">Last processed:</Text>
                                        <div className="font-medium">{formatDateTime(data.psdProcessedAt)}</div>
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Processing Status Message */}
                        {isProcessing && (
                            <Card
                                size="small"
                                className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                                        <RefreshCw className="size-4 animate-spin" />
                                        <span>AI is processing the PDS text. This may take a few moments.</span>
                                    </div>
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={handleCancelProcessing}
                                        loading={isCancelling}
                                        className="text-blue-700 dark:text-blue-300"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Card>
                        )}

                        {/* Input Method Toggle */}
                        {!isProcessing && !showPreview && (
                            <SegmentedControl
                                value={inputMethod}
                                onChange={setInputMethod}
                                size="middle"
                                block
                                options={[
                                    { value: 'upload', label: 'Upload PDF', icon: <UploadIcon size={16} /> },
                                    { value: 'paste', label: 'Paste text', icon: <FileText size={16} /> },
                                ]}
                                aria-label="Select input method"
                            />
                        )}

                        {/* PDF Upload Section */}
                        {inputMethod === 'upload' && !showPreview && !isProcessing && (
                            <div className="space-y-4">
                                <Dragger {...uploadProps} disabled={isExtracting}>
                                    <p className="ant-upload-drag-icon">
                                        <Inbox size={48} className="mx-auto text-accent-500" />
                                    </p>
                                    <p className="ant-upload-text">Click or drag PDF file to this area</p>
                                    <p className="ant-upload-hint">
                                        Upload your Product Disclosure Statement PDF. The content will be extracted and
                                        converted to text for processing.
                                    </p>
                                </Dragger>

                                {fileList.length > 0 && (
                                    <div className="mt-3 flex items-center justify-between rounded-lg bg-muted p-3">
                                        <div className="flex items-center gap-3">
                                            <FileText className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <Text strong>{fileList[0].name}</Text>
                                                <br />
                                                <Text type="secondary" className="text-xs">
                                                    {((fileList[0].size ?? 0) / 1024 / 1024).toFixed(2)} MB
                                                </Text>
                                            </div>
                                        </div>
                                        {!isExtracting && (
                                            <Button type="danger" size="small" onClick={() => setFileList([])}>
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {showError && (
                                    <Alert
                                        type="error"
                                        title="Extraction failed"
                                        description={extractionState.message}
                                        showIcon
                                        closable
                                        onClose={() => setExtractionState({ status: 'idle' })}
                                    />
                                )}

                                <div className="mt-4 flex justify-end">
                                    <Button
                                        type="primary"
                                        icon={<UploadIcon size={16} />}
                                        onClick={handleUploadAndExtract}
                                        loading={isExtracting}
                                        disabled={fileList.length === 0}
                                    >
                                        {extractionState.status === 'uploading'
                                            ? 'Uploading...'
                                            : extractionState.status === 'extracting'
                                              ? 'Extracting content...'
                                              : 'Extract content'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Extraction Preview */}
                        {showPreview && (
                            <div className="space-y-4">
                                <Alert
                                    type="success"
                                    title="PDF content extracted successfully"
                                    description={
                                        <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1 text-sm md:grid-cols-4">
                                            <div>
                                                <Text type="secondary">Pages:</Text>{' '}
                                                <Text strong>{extractionState.metadata.pageCount}</Text>
                                            </div>
                                            <div>
                                                <Text type="secondary">Tables:</Text>{' '}
                                                <Text strong>{extractionState.metadata.tableCount}</Text>
                                            </div>
                                            <div>
                                                <Text type="secondary">Paragraphs:</Text>{' '}
                                                <Text strong>{extractionState.metadata.paragraphCount}</Text>
                                            </div>
                                            <div>
                                                <Text type="secondary">Characters:</Text>{' '}
                                                <Text strong>
                                                    {formatNumber(extractionState.metadata.characterCount, {
                                                        maximumFractionDigits: 0,
                                                    })}
                                                </Text>
                                            </div>
                                        </div>
                                    }
                                    showIcon
                                    icon={<Check size={16} />}
                                />

                                <Card
                                    size="small"
                                    title={
                                        <div className="flex items-center justify-between">
                                            <span>Extracted content preview</span>
                                            <Button
                                                type="tertiary"
                                                size="small"
                                                icon={isEditingPreview ? <Check size={14} /> : <Pencil size={14} />}
                                                onClick={() => setIsEditingPreview(!isEditingPreview)}
                                            >
                                                {isEditingPreview ? 'Done editing' : 'Edit'}
                                            </Button>
                                        </div>
                                    }
                                >
                                    {isEditingPreview ? (
                                        <TextArea
                                            value={previewEditText}
                                            onChange={(e) => setPreviewEditText(e.target.value)}
                                            rows={15}
                                            className="resize-y font-mono text-sm"
                                        />
                                    ) : (
                                        <div className="max-h-96 overflow-auto">
                                            <Markdown variant="document">{extractionState.markdown}</Markdown>
                                        </div>
                                    )}
                                </Card>

                                <div className="flex justify-end gap-3">
                                    <Button type="secondary" icon={<X size={16} />} onClick={handleCancelExtraction}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<Check size={16} />}
                                        onClick={handleUseExtractedContent}
                                    >
                                        Use this content
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Raw Text Input (Paste mode) */}
                        {inputMethod === 'paste' && !showPreview && !isProcessing && (
                            <div>
                                <div className="mb-2 flex items-center justify-between">
                                    <Text strong>Raw PDS text</Text>
                                    {hasChanges && (
                                        <Text type="warning" className="text-sm">
                                            Unsaved changes
                                        </Text>
                                    )}
                                </div>
                                <Paragraph type="secondary" className="mb-3 text-sm">
                                    Enter the Product Disclosure Statement text below. This will be processed by AI to
                                    create an optimised format for claim compliance assessment.
                                </Paragraph>
                                <TextArea
                                    value={rawText}
                                    onChange={(e) => setRawText(e.target.value)}
                                    rows={15}
                                    placeholder="Paste or type the Product Disclosure Statement text here..."
                                    className="resize-y font-mono text-sm"
                                    disabled={isProcessing}
                                />
                            </div>
                        )}

                        {/* Processed Text Preview */}
                        {data?.hasProcessedText && (
                            <Collapse
                                items={[
                                    {
                                        key: 'processed',
                                        label: (
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">Processed PDS text</span>
                                                <Text type="secondary" className="text-sm">
                                                    (AI-optimised for analysis)
                                                </Text>
                                            </div>
                                        ),
                                        children: (
                                            <div className="max-h-96 overflow-auto">
                                                <pre className="whitespace-pre-wrap font-mono text-muted-foreground text-sm">
                                                    {data.psdProcessedText}
                                                </pre>
                                            </div>
                                        ),
                                    },
                                ]}
                                bordered={false}
                                expandIcon={({ isActive }) => (
                                    <ChevronDown
                                        className={`h-4 w-4 transition-transform ${isActive ? 'rotate-180' : ''}`}
                                    />
                                )}
                                className="bg-muted"
                            />
                        )}

                        {/* Empty State for Processed Text */}
                        {!data?.hasProcessedText && data?.hasRawText && (
                            <Card size="small" className="border-dashed">
                                <EmptyState
                                    size="small"
                                    icon={<FileText />}
                                    title="No processed text yet"
                                    description="Save the raw text to trigger AI processing."
                                />
                            </Card>
                        )}
                    </div>
                )}
            </QueryState>
        </OverlayPage>
    );
}
